import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api } from '@/lib/api';
import { useToast } from '@/contexts/ToastContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { FormError } from '@/components/ui/form-error';
import { PageLoading } from '@/components/ui/loading';
import { DatePicker } from '@/components/ui/date-picker';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogAction, AlertDialogCancel } from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { nameRule, optionalText, REQUIRED_FIELDS_MESSAGE } from '@/lib/validation-rules';
import { focusFirstInvalidField, handleApiSubmitError } from '@/lib/form-validation';
import { getBusinessDate } from '@shared/project-availability';

const schema = z.object({
  name: nameRule('Project Name', 200, 2),
  clientName: optionalText(200),
  siteLocation: optionalText(500),
  projectStartDate: z.string().min(1, 'Project Start Date is required'),
  projectCloseDate: z.string().min(1, 'Project Close Date is required'),
  description: optionalText(2000),
  status: z.string().optional(),
}).superRefine((data, ctx) => {
  if (data.projectStartDate && data.projectCloseDate && data.projectStartDate > data.projectCloseDate) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['projectCloseDate'], message: 'Close Date cannot be earlier than Start Date' });
  }
});
type FormData = z.infer<typeof schema>;

export default function ProjectFormPage() {
  const { id } = useParams(); const navigate = useNavigate(); const { addToast } = useToast(); const isEdit = !!id;
  const [pendingSubmit, setPendingSubmit] = useState<FormData | null>(null);
  const { register, handleSubmit, setValue, reset, setError, formState: { errors } } = useForm<FormData>({ resolver: zodResolver(schema) });
  const onInvalid = (errs: FieldErrors<FormData>) => {
    focusFirstInvalidField(errs);
    addToast(REQUIRED_FIELDS_MESSAGE, 'error');
  };
  const { data: existing, isLoading: loadingExisting } = useQuery({ queryKey: ['project', id], queryFn: async () => { const r = await api.get<{ data: any }>(`/projects/${id}`); return r.data; }, enabled: isEdit });

  useEffect(() => {
    if (existing) reset({
      name: existing.name,
      clientName: existing.client_name || '',
      siteLocation: existing.site_location || '',
      projectStartDate: existing.project_start_date ?? existing.start_date ?? '',
      projectCloseDate: existing.project_close_date ?? existing.expected_end_date ?? '',
      description: existing.description || '',
      status: existing.status,
    });
  }, [existing, reset]);

  const mutation = useMutation({
    mutationFn: (d: any) => isEdit ? api.put(`/projects/${id}`, d) : api.post('/projects', d),
    onSuccess: () => { addToast(`Project ${isEdit ? 'updated' : 'created'}`, 'success'); navigate('/admin/projects'); },
    onError: (err: any) => handleApiSubmitError(err, { addToast, setError: (name, error) => setError(name as any, error) }),
  });

  const onSubmit = (d: FormData) => {
    // Closing a project: warn before saving a Close Date that is already in
    // the past, because the project leaves the operational dropdowns.
    if (isEdit && d.projectCloseDate && getBusinessDate() > d.projectCloseDate) {
      setPendingSubmit(d);
      return;
    }
    mutation.mutate(d);
  };

  if (isEdit && loadingExisting) return <PageLoading />;
  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-heading font-bold text-primary">{isEdit ? 'Edit Project' : 'Create Project'}</h1>
      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6">
        <Card><CardContent className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="min-w-0">
            <Label htmlFor="project-name">Project Name *</Label>
            <Input
              id="project-name"
              {...register('name')}
              aria-invalid={errors.name ? true : undefined}
              aria-describedby={errors.name ? 'name-error' : undefined}
              className={cn(errors.name && 'border-error')}
            />
            <FormError id="name-error" message={errors.name?.message} />
          </div>
          <div className="min-w-0"><Label htmlFor="client-name">Client</Label><Input id="client-name" {...register('clientName')} /></div>
          <div className="min-w-0"><Label htmlFor="site-location">Site Location</Label><Input id="site-location" {...register('siteLocation')} /></div>
          <div className="min-w-0">
            <Label htmlFor="project-start-date">Project Start Date *</Label>
            <DatePicker id="project-start-date" {...register('projectStartDate')} aria-invalid={errors.projectStartDate ? true : undefined} aria-describedby={errors.projectStartDate ? 'projectStartDate-error' : undefined} className={cn(errors.projectStartDate && 'border-error')} />
            <FormError id="projectStartDate-error" message={errors.projectStartDate?.message} />
          </div>
          <div className="min-w-0">
            <Label htmlFor="project-close-date">Project Close Date *</Label>
            <DatePicker id="project-close-date" {...register('projectCloseDate')} aria-invalid={errors.projectCloseDate ? true : undefined} aria-describedby={errors.projectCloseDate ? 'projectCloseDate-error' : undefined} className={cn(errors.projectCloseDate && 'border-error')} />
            <FormError id="projectCloseDate-error" message={errors.projectCloseDate?.message} />
          </div>
          {isEdit && <div className="md:col-span-2"><Label htmlFor="project-status">Status</Label><Select onValueChange={v => setValue('status', v)} defaultValue={existing?.status}><SelectTrigger id="project-status" data-field-name="status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="on_hold">On Hold</SelectItem><SelectItem value="completed">Completed</SelectItem><SelectItem value="archived">Archived</SelectItem></SelectContent></Select></div>}
          <div className="md:col-span-2"><Label htmlFor="project-description">Description</Label><Textarea id="project-description" {...register('description')} /></div>
        </CardContent></Card>
        <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
          <Button type="button" variant="outline" className="w-full sm:w-auto min-h-[44px]" onClick={() => navigate('/admin/projects')}>Cancel</Button>
          <Button type="submit" loading={mutation.isPending} className="w-full sm:w-auto min-h-[44px]">{isEdit ? 'Update' : 'Create'}</Button>
        </div>
      </form>
      <AlertDialog open={!!pendingSubmit} onOpenChange={(open) => { if (!open) setPendingSubmit(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close this project?</AlertDialogTitle>
            <AlertDialogDescription>
              The Project Close Date {pendingSubmit?.projectCloseDate} is already in the past. Saving will close this project and remove it from operational dropdowns. Existing historical records remain unchanged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-warning text-[#081631] hover:opacity-90" onClick={() => { if (pendingSubmit) mutation.mutate(pendingSubmit); setPendingSubmit(null); }}>Save &amp; Close</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
